import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton
} from '@invent/wl-ui-kit-next/ui';
import { CheckCircle2 } from 'lucide-react';
import * as React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { HelpPopover, PageHeader } from '@/design';
import { errorMessage } from '@/domains/api';
import { surveyApi } from '../api';
import type {
  IAnswer,
  IDefinitionSnapshot,
  IResponseState,
  ISnapshotUsage
} from '../api/contracts';
import {
  addressKey,
  answerInstanceKey,
  buildDefinitionView,
  effectiveLabel,
  formatAnswer,
  indexAnswers,
  isChildTriggered,
  isSectionVisible,
  isUsageVisible,
  selectedCodes,
  type IUsageNode
} from '../runtime';
import { PORTAL_PATHS, responseFormPath } from '../paths';
import { surveyTitle } from '../survey-labels';
import { SubmissionDownloadButton } from './submission-download';

/**
 * Receipt and read-back after a submit.
 *
 * Re-reads the state rather than carrying it through navigation: submit runs a
 * full evaluation pass server-side, and the document it produced is the one
 * record of what was actually filed.
 *
 * Drawn in the Advisor Portal's system (the two-portals design, §7): the confirmation first, with
 * the reference a respondent quotes to Veritant, then the read-back, question beside answer, section by
 * section. Its words are the ones it had — "What you submitted", "Review or correct answers".
 *
 * Excluded answers are left out. `isExcluded` is the engine's own derivation — a
 * rule-hidden ancestor, an instance that shrank out of the count — and it is the
 * same flag reporting and the PDF export read through, so showing them here
 * would tell the respondent something no downstream consumer agrees with.
 */
export function ResponseSubmittedPage() {
  const { responseId } = useParams<{ responseId: string }>();
  const [state, setState] = React.useState<Nullable<IResponseState>>(null);
  const [definition, setDefinition] = React.useState<Nullable<IDefinitionSnapshot>>(null);
  const [error, setError] = React.useState<Nullable<string>>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (!responseId) return undefined;
    const controller = new AbortController();

    (async () => {
      try {
        const [loadedState, loadedDefinition] = await Promise.all([
          surveyApi.getResponse(responseId, controller.signal),
          surveyApi.getDefinition(responseId, controller.signal)
        ]);
        if (controller.signal.aborted) return;
        setState(loadedState);
        setDefinition(loadedDefinition);
      } catch (loadError) {
        if (!controller.signal.aborted) {
          setError(errorMessage(loadError, 'Could not load your submission'));
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [responseId]);

  const view = React.useMemo(
    () => (definition ? buildDefinitionView(definition) : null),
    [definition]
  );
  const answers = React.useMemo(() => indexAnswers(state?.answers ?? []), [state]);

  // A draft has nothing to receipt. Reachable by typing the URL or by keeping a
  // stale link after a response went back to draft (restored from the recycle bin as one,
  // say), and rendering the page anyway would thank the respondent for a submission that
  // never happened.
  if (state && state.status === 'draft') {
    return <Navigate to={responseFormPath(state.responseId)} replace />;
  }

  // Correcting is only on the table while the response is still writable; once
  // the cycle locks it, offering "submit again" would promise what the API
  // refuses.
  const canCorrect = state?.status === 'submitted';

  return (
    <>
      <PageHeader
        crumb={definition ? `${surveyTitle(definition.surveyCode)} survey` : undefined}
        title="Submission received"
      />

      <div className="mx-auto grid w-full max-w-[1120px] gap-6 px-6 pt-6 pb-16 lg:px-10">
        {isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : error || !state || !definition || !view ? (
          <Alert variant="destructive">
            <AlertTitle>We could not load that submission</AlertTitle>
            <AlertDescription>{error ?? 'The response is no longer available.'}</AlertDescription>
          </Alert>
        ) : (
          <>
            <Card className="border-border border-l-4 border-l-[var(--color-teal)]">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-xl font-normal text-[var(--color-royal-blue)]">
                  <CheckCircle2 className="size-6 shrink-0 text-[var(--color-teal)]" aria-hidden />
                  Thank you — your {definition.cycle.code} {surveyTitle(definition.surveyCode)}{' '}
                  survey is in
                </CardTitle>
                <CardDescription>
                  {canCorrect
                    ? 'Need to change something? Reopen it and submit again — every submission is kept, and research works from the latest.'
                    : `This response is ${state.status}, so it can be read but no longer changed.`}
                </CardDescription>
              </CardHeader>

              <CardContent className="grid gap-6">
                <dl className="grid gap-4 rounded-lg bg-[var(--color-off-white)] px-5 py-4 text-sm sm:grid-cols-[minmax(0,1fr)_auto]">
                  <div>
                    <dt className="text-muted-foreground">Your reference</dt>
                    <dd className="font-mono text-xs break-all">{state.responseId}</dd>
                  </div>
                  {/* No survey version and no revision: a version id means nothing to the
                      respondent, and on a receipt a revision reads as "submission #16" while it
                      counts saved fields. The response id is what identifies this filing to
                      support. */}
                  <div>
                    <dt className="text-muted-foreground">Status</dt>
                    <dd>
                      <Badge variant="success" className="capitalize">
                        {state.status}
                      </Badge>
                    </dd>
                  </div>
                </dl>

                <div className="flex flex-wrap items-start gap-3">
                  {/* The PDF of this submission, for the advisor's own compliance file (SUR-10):
                      ready the moment the submit returns, and the same file the confirmation
                      mail carries (SR-497). */}
                  <div className="flex items-start gap-1">
                    <SubmissionDownloadButton
                      responseId={state.responseId}
                      surveyCode={definition.surveyCode}
                      cycleCode={definition.cycle.code}
                      label="Download PDF"
                    />
                    <HelpPopover label="About the PDF of your submission" className="mt-2">
                      <p>
                        The PDF shows the questions and your answers as you submitted them. Keep it
                        for your firm&apos;s compliance records.
                      </p>
                      <p>
                        Each submission also sends a confirmation email with this PDF attached — to you,
                        to the advisor when someone submits for them, and to the admin or assistant
                        email given in the survey, if there is one. Every version you submit stays in
                        Documents.
                      </p>
                    </HelpPopover>
                  </div>
                  {canCorrect && (
                    // `?edit=1` is what makes the form open at all for a
                    // submitted response — without it the form sends the
                    // respondent straight back here.
                    <Button asChild variant="outline">
                      <Link to={`${responseFormPath(state.responseId)}?edit=1`}>
                        Review or correct answers
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="ghost">
                    <Link to={PORTAL_PATHS.surveys}>Back to your surveys</Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>What you submitted</CardTitle>
                <CardDescription>
                  Questions that did not apply to your answers are not listed.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-8">
                {view.sections
                  .filter((section) => isSectionVisible(section, state.render))
                  .map((section) => {
                    const rows = section.roots
                      .filter((node) => isUsageVisible(node.usage.code, state.render))
                      .flatMap((node) => [node, ...triggeredChildren(node, answers)])
                      .map((node) => ({ node, answer: answers.get(answerKeyOf(node.usage)) }))
                      .filter(({ answer }) => answer && !answer.isExcluded);

                    if (rows.length === 0) return null;

                    return (
                      <section key={section.section.code}>
                        <h3 className="mb-1 border-b border-border pb-2 text-lg font-normal text-[var(--color-royal-blue)]">
                          {section.section.title}
                        </h3>
                        <dl className="grid text-[0.9375rem]">
                          {rows.map(({ node, answer }) => (
                            <div
                              key={node.usage.code}
                              className="grid gap-1 border-b border-border py-3 last:border-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-8"
                            >
                              <dt className="text-muted-foreground">
                                {effectiveLabel(node.usage, state.render)}
                              </dt>
                              <dd className="break-words text-[var(--color-midnight-blue)]">
                                {formatAnswer(
                                  answer!.value,
                                  view.optionLabels.get(node.usage.code)
                                )}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      </section>
                    );
                  })}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </>
  );
}

/**
 * A question's sub-questions, minus any whose trigger option the respondent left
 * unselected — the same gate the form applies (`isChildTriggered`), for the same reason.
 *
 * A `per_option_sub`'s stored answer outlives its trigger: deselect the option and the
 * form stops rendering the box, but nothing marks the answer excluded, because the engine
 * reports these usages visible unconditionally — that gap is exactly why `isChildTriggered`
 * exists at all. So `isExcluded`, which every other row here is filtered by, cannot catch
 * this one, and the receipt would otherwise print an answer to a question that was not
 * asked at submit time. Reachable only since these answers started resolving at their
 * trigger code (`answerInstanceKey`); the blank-key lookup this replaced missed them
 * whether or not their trigger was still selected.
 */
function triggeredChildren(node: IUsageNode, answers: Map<string, IAnswer>): IUsageNode[] {
  const selection = selectedCodes(answers.get(answerKeyOf(node.usage)));
  return node.children.filter((child) => isChildTriggered(child.usage, selection));
}

/**
 * Where one usage's own answer sits in the indexed state, for this page's two readers.
 *
 * `instanceId` is null because the receipt walks roots and their children only — never a
 * repeat row — and the third segment is **not** a literal `''`: `children` includes every
 * `per_option_sub`, and a scalar one's answer lives at its trigger option code
 * (`answerInstanceKey`, `snapshot-view.ts`), which is the write address
 * `AnswerWriteService` enforces. A blank lookup here missed all 24 "Other, please specify"
 * answers on this, the respondent's own read-back of what they submitted.
 */
function answerKeyOf(usage: ISnapshotUsage): string {
  return addressKey(usage.code, null, answerInstanceKey(usage));
}
